import { useState, useCallback, useRef, CSSProperties, useLayoutEffect, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import { PDFViewer } from './pdfviewer';
import { PreviewParameters } from './pdfviewer';

export interface FilePreviewProps {
    fileName: string;
    url: string;
    pdfViewMethod: 'canvas' | 'iframes';
}

export enum FetchState {
    Working,
    Done,
    Error
}

const defaultParameters: PreviewParameters = {
    fitToScreen: 'height',
    rotate: 0,
    scale: 1
};

const pdfStyled: CSSProperties = { width: '100%', height: '100%' };

const Container = styled('div')(({ theme }) => ({
    position: 'relative',
    background: theme.palette.grey[700],
    flex: 1,
    flexDirection: 'column',
    display: 'flex',
    justifyContent: 'center',
    overflow: 'hidden'
}));

const Buttons = styled('div')(({ theme }) => ({
    position: 'absolute',
    bottom: 48,
    right: 30,
    '& > button': {
        display: 'block',
        margin: 8,
        padding: 10,
        backgroundColor: theme.palette.grey[200],
        boxShadow: '0px 0px 2px rgb(0, 0, 0, 0.12), 0px 2px 2px rgb(0, 0, 0, 0.24)',
        fontSize: 0,
        '&:hover': {
            backgroundColor: theme.palette.grey[100]
        }
    },
    '& svg': {
        width: 20,
        height: 20
    }
}));

const SimpleBarContent = styled('div')({
    height: '100%',
    width: '100%',
    '& .simplebar-content': {
        minHeight: '100%',
        minWidth: '100%'
    }
});

const getPrintContentHTML = (fileName: string, url: string) =>
    `<html style="height: 100%;"><head><meta name="viewport" content="width=device-width, minimum-scale=0.1"><title>${fileName}</title></head><body style="margin: 0px; height: 100%"><img style="max-height: 100vh; max-width: 100vw;" src="${url}"></body></html>`;

export const FilePreview = ({ fileName, url, pdfViewMethod }: FilePreviewProps) => {
    const [, setLoading] = useState<FetchState>(FetchState.Done);

    const [parameters, setParameters] = useState<PreviewParameters>(defaultParameters);

    useLayoutEffect(() => setLoading(FetchState.Working), [url]);
    useEffect(() => setParameters(defaultParameters), [url]);

    const changeFitScreen = useCallback(() => {
        setParameters(p => ({
            ...p,
            scale: 1,
            fitToScreen: p.fitToScreen === 'width' ? 'height' : 'width'
        }));
    }, []);

    const scaleInc = useCallback(() => {
        setParameters(p => ({
            ...p,
            scale: p.scale + 0.5
        }));
    }, []);

    const scaleDec = useCallback(() => {
        setParameters((p: any) => ({
            ...p,
            scale: p.scale - 0.5
        }));
    }, []);

    const printIFrameRef = useRef<HTMLIFrameElement>();

    const onLoad = useCallback(async () => {
        setLoading(FetchState.Done);
        if (url && fileName && !fileName.toLowerCase()?.endsWith('.pdf')) {
            const document = printIFrameRef.current?.contentDocument;
            if (document) {
                document.open();
                document.write(getPrintContentHTML(fileName, url));
                document.close();
            }
        }
    }, [url, fileName]);
    const onError = useCallback(() => {
        console.log('Set working - error');
        setLoading(FetchState.Error);
    }, []);

    const refSimpleBar = useRef();
    return (
        <Box
            sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                position: 'relative',
                flexDirection: 'column',
                overflow: 'auto'
            }}
        >
            {fileName?.toLowerCase()?.endsWith('.pdf') ? (
                <iframe
                    title='PDF preview'
                    src={`${url}${'#toolbar=0&view=Fit'}`}
                    onLoad={onLoad}
                    onError={onError}
                    style={pdfStyled}
                />
            ) : (
                <>
                    <Container>
                        <SimpleBarContent ref={refSimpleBar as any}>
                            {fileName?.toLowerCase()?.endsWith('.pdf') ? (
                                pdfViewMethod === 'canvas' ? (
                                    <PDFViewer
                                        url={url}
                                        style={pdfStyled}
                                        onLoad={onLoad}
                                        onError={onError}
                                        parameters={parameters}
                                    />
                                ) : (
                                    <iframe
                                        title='PDF preview'
                                        src={url}
                                        style={pdfStyled}
                                        onLoad={onLoad}
                                        onError={onError}
                                    />
                                )
                            ) : pdfViewMethod === 'canvas' ? (
                                <PDFViewer
                                    url={url}
                                    style={pdfStyled}
                                    onLoad={onLoad}
                                    onError={onError}
                                    parameters={parameters}
                                />
                            ) : (
                                <iframe
                                    title='PDF preview'
                                    src={url}
                                    style={pdfStyled}
                                    onLoad={onLoad}
                                    onError={onError}
                                />
                            )}
                        </SimpleBarContent>
                        <Buttons>
                            <IconButton onClick={changeFitScreen} style={{ marginBottom: 24 }}>
                                {parameters?.fitToScreen === 'height' ? <FullscreenIcon /> : <FullscreenExitIcon />}
                            </IconButton>
                            <IconButton onClick={scaleInc}>
                                <AddIcon />
                            </IconButton>
                            <IconButton onClick={scaleDec} disabled={parameters.scale <= 0.5}>
                                <RemoveIcon />
                            </IconButton>
                        </Buttons>
                    </Container>
                </>
            )}
        </Box>
    );
};
